/**
 * Chart building blocks.
 *
 * Two rules encoded here rather than left to each chart:
 *   • marks read their colour from `--chart-*` tokens, so light/dark are two selected
 *     palettes rather than one flipped one
 *   • every chart ships a table view — the accessible fallback, and the thing an
 *     operator actually wants when they need the exact figure rather than the shape
 */

import * as React from 'react';
import { Table2, ChartColumn } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/misc';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { EmptyState } from '@/components/common/page-header';
import { useI18n } from '@/lib/i18n';
import { cn } from '@/lib/utils';

/** Reads a resolved token value — Recharts needs a concrete colour, not a var(). */
export function useChartColors() {
  const [colors, setColors] = React.useState<Record<string, string>>({});

  React.useEffect(() => {
    const read = () => {
      const styles = getComputedStyle(document.documentElement);
      const get = (name: string) => styles.getPropertyValue(name).trim();
      setColors({
        series1: get('--chart-1'),
        series2: get('--chart-2'),
        series3: get('--chart-3'),
        series4: get('--chart-4'),
        series5: get('--chart-5'),
        muted: get('--chart-muted'),
        grid: get('--chart-grid'),
        axis: get('--chart-axis'),
        surface: get('--card'),
      });
    };

    read();
    // The theme toggle rewrites the tokens; observe the class so marks recolour with it.
    const observer = new MutationObserver(read);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  return colors;
}

interface ChartCardProps {
  title: string;
  description?: string;
  isLoading?: boolean;
  isEmpty?: boolean;
  /** Rendered when the reader switches to the table view. */
  table?: React.ReactNode;
  action?: React.ReactNode;
  height?: number;
  /**
   * Let the content decide the height instead of reserving `height` for it.
   *
   * Recharts needs a definite height — its ResponsiveContainer measures the parent — and so
   * does anything sizing bars in `%`. A list of horizontal bars needs neither: its rows are
   * as tall as they are. Reserving 300px for eight 24px rows is invisible on a desktop grid
   * and glaring on a phone, where the card then centres a third of a screen of content in a
   * full screen of card. `height` still drives the loading skeleton, so the card does not
   * change size when the data lands.
   */
  fitContent?: boolean;
  children: React.ReactNode;
  className?: string;
}

export function ChartCard({
  title,
  description,
  isLoading,
  isEmpty,
  table,
  action,
  height = 280,
  fitContent,
  children,
  className,
}: ChartCardProps) {
  const { t } = useI18n();
  const [showTable, setShowTable] = React.useState(false);

  return (
    <Card className={className}>
      <CardHeader>
        <div className="min-w-0">
          <CardTitle>{title}</CardTitle>
          {description && <CardDescription className="mt-0.5">{description}</CardDescription>}
        </div>
        <CardAction>
          {action}
          {table && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setShowTable((current) => !current)}
              aria-label={showTable ? t('dashboard.showChart') : t('dashboard.showTable')}
              title={showTable ? t('dashboard.showChart') : t('dashboard.showTable')}
            >
              {showTable ? <ChartColumn /> : <Table2 />}
            </Button>
          )}
        </CardAction>
      </CardHeader>

      <CardContent className="pt-1">
        {isLoading ? (
          <Skeleton style={{ height }} className="w-full" />
        ) : isEmpty ? (
          <div style={{ minHeight: height }} className="flex items-center justify-center">
            <EmptyState title={t('common.empty')} compact />
          </div>
        ) : showTable && table ? (
          <div style={{ maxHeight: height + 40 }} className="overflow-y-auto">
            {table}
          </div>
        ) : fitContent ? (
          <div>{children}</div>
        ) : (
          <div style={{ height }}>{children}</div>
        )}
      </CardContent>
    </Card>
  );
}

/** Recharts tooltip styled with app tokens instead of its defaults. */
export function ChartTooltip({
  active,
  payload,
  label,
  formatter,
}: {
  active?: boolean;
  payload?: { name?: string; dataKey?: string; value?: number; color?: string }[];
  label?: string;
  formatter?: (value: number) => string;
}) {
  if (!active || !payload?.length) return null;

  return (
    <div className="bg-popover text-popover-foreground rounded-lg border px-3 py-2 text-xs shadow-lg">
      {label && <p className="mb-1.5 font-medium">{label}</p>}
      <ul className="space-y-1">
        {payload.map((entry, index) => (
          <li key={index} className="flex items-center gap-2 whitespace-nowrap">
            <span
              className="size-2 shrink-0 rounded-[2px]"
              style={{ backgroundColor: entry.color }}
              aria-hidden
            />
            <span className="text-muted-foreground">{entry.name}</span>
            <span className="ml-auto font-medium tabular">
              {formatter && typeof entry.value === 'number' ? formatter(entry.value) : entry.value}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Legend. Always rendered for two or more series, because identity must never rest on
 * colour alone — the swatch sits *beside* the label, and the label wears text ink.
 */
export function ChartLegend({
  items,
  className,
}: {
  items: { label: string; color: string }[];
  className?: string;
}) {
  return (
    <ul className={cn('flex flex-wrap items-center gap-x-4 gap-y-1.5', className)}>
      {items.map((item) => (
        <li key={item.label} className="text-muted-foreground flex items-center gap-1.5 text-xs">
          <span
            className="size-2.5 shrink-0 rounded-[3px]"
            style={{ backgroundColor: item.color }}
            aria-hidden
          />
          {item.label}
        </li>
      ))}
    </ul>
  );
}

/** Generic table view for any chart series. */
export function ChartTable({
  columns,
  rows,
}: {
  columns: string[];
  rows: (string | number)[][];
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {columns.map((column, index) => (
            <TableHead key={column} className={index > 0 ? 'text-right' : undefined}>
              {column}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row, rowIndex) => (
          <TableRow key={rowIndex}>
            {row.map((cell, cellIndex) => (
              <TableCell
                key={cellIndex}
                className={cn('py-1.5', cellIndex > 0 && 'text-right tabular')}
              >
                {cell}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export const AXIS_STYLE = { fontSize: 11 } as const;
